import {AxiosRequestConfig} from 'axios'
import RequestBase from '../../../api/RequestBase'
import RequestBaseTemu from '../../../api/RequestBaseTemu'

const URL_SHIPPING_ADDRESS = '/shipping-addresses'
const URL_SHIPPING_PRESETS = '/shipping-presets'
const URL_STORE = '/connections'
const URL_PROMOTION = '/promotion'
const URL_PACKAGES = '/packages'
const URL_SHIPSTATION = `/shipstation`
const URL_USERS = `/users`
const URL_COMPANIES = `/companies`
const PACKING_SLIP = `/settings`
const URL_NOTIFICATION = '/notifications'
const URL_FUNDING = '/payments/funding-preferences'
const URL_TWO_FACTOR_AUTH = '/2fa'
const URL_SETTING_INSURANCE = '/settings/insurance'
const URL_SETTING_CARRIER_ROUTE = '/settings/carrier-route'

// addresses
const getAddress = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_SHIPPING_ADDRESS}`, config)
}

const saveAddress = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPPING_ADDRESS}`, payload, config)
}

const editAddress = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_SHIPPING_ADDRESS}`, payload, config)
}

const deleteAddress = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPPING_ADDRESS}/delete`, payload, config)
}

// packages
const getPackages = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_PACKAGES}`, config)
}

const getPackageDetail = (id?: string, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_PACKAGES}/${id}`, config)
}

const createPackages = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_PACKAGES}`, payload, config)
}

const editPackage = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_PACKAGES}`, payload, config)
}

const deletePackage = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_PACKAGES}/delete`, payload, config)
}

// presets
const getShippingPresets = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_SHIPPING_PRESETS}`, config)
}

const getDefaultPreset = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_SHIPPING_PRESETS}/default`, config)
}

const createShippingPreset = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPPING_PRESETS}`, payload, config)
}

const updateShippingPreset = (
  id: string,
  payload: any,
  config?: AxiosRequestConfig | undefined
) => {
  return RequestBase.put(`${URL_SHIPPING_PRESETS}/${id}`, payload, config)
}

const setDefaultPreset = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPPING_PRESETS}/default`, payload, config)
}

const applyShippingPreset = (payload?: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_SHIPPING_PRESETS}/apply`, payload, config)
}

const deleteShippingPreset = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPPING_PRESETS}/delete`, payload, config)
}

// store
const getConfig = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_STORE}/configurations`, config)
}

const frontToken = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SHIPSTATION}/front-token`, payload, config)
}

const connections = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_STORE}`, config)
}

const connect = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_STORE}`, payload, config)
}

const disconnect = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.remove(`${URL_STORE}`, config)
}

const removeStore = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.remove(`${URL_STORE}/remove`, config)
}

const getShopifyIntegrateLink = (shopUrl, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get('/shopify/install-link?shop=' + shopUrl, config)
}

const getEbayConsentLink = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get('/ebay/consent-link', config)
}

const webhookUrl = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_STORE}/webhook-url`, config)
}

const checkPromoEligibility = (promoType?: string, config?: AxiosRequestConfig | undefined) => {
  const url = promoType
    ? `${URL_PROMOTION}/eligibility/${promoType}`
    : `${URL_PROMOTION}/eligibility`
  return RequestBase.get(url, config)
}

const applyPromoCode = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_PROMOTION}/apply-code`, payload, config)
}

// profile
const accountEdit = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_USERS}/profile`, payload, config)
}

const changePassword = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_USERS}/profile/change-password`, payload, config)
}

const getCompany = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(URL_COMPANIES, config)
}

const changePlan = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_USERS}/profile/change-plan`, payload, config)
}

// general
const updateGeneral = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_USERS}/profile/general`, payload, config)
}

const getNotification = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(URL_NOTIFICATION, config)
}

const createNotification = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(URL_NOTIFICATION, payload, config)
}

const updateNotification = (id, payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_NOTIFICATION}/${id}`, payload, config)
}

// settings
const settings = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`/settings`, config)
}

const updatePrint = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${PACKING_SLIP}/print`, payload, config)
}

const updatePackingSlip = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${PACKING_SLIP}/packing-slip`, payload, config)
}

const updateReport = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${PACKING_SLIP}/report`, payload, config)
}

const updateManifest = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${PACKING_SLIP}/manifest`, payload, config)
}

const get2FASettings = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_TWO_FACTOR_AUTH}/settings`, config)
}

const update2FASettings = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_TWO_FACTOR_AUTH}/settings`, payload, config)
}

const addPhoneNumber = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_TWO_FACTOR_AUTH}/phone/add`, payload, config)
}

const removePhoneNumber = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.remove(`${URL_TWO_FACTOR_AUTH}/phone/remove`, {
    ...config,
    data: payload,
  })
}

const setDefaultPhoneNumber = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_TWO_FACTOR_AUTH}/phone/default`, payload, config)
}

// Specially handle for Temu integration
const connectTemuStore = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBaseTemu.post(`${URL_STORE}`, payload, config)
}
const getSettingInsurance = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_SETTING_INSURANCE}`, config)
}
const postSettingInsurance = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_SETTING_INSURANCE}`, payload, config)
}
const putSettingInsurance = (id: any, payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_SETTING_INSURANCE}/${id}`, payload, config)
}

const getFundingPreference = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_FUNDING}`, config)
}
const createFundingSetting = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.post(`${URL_FUNDING}`, payload, config)
}
const updateFundingSetting = (id, payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_FUNDING}/${id}`, payload, config)
}

// carrier route
const getCarrierRoute = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${URL_SETTING_CARRIER_ROUTE}`, config)
}

const updateCarrierRoute = (payload: any, config?: AxiosRequestConfig | undefined) => {
  return RequestBase.put(`${URL_SETTING_CARRIER_ROUTE}`, payload, config)
}

// account integrations
const getAccountIntegrations = (config?: AxiosRequestConfig | undefined) => {
  return RequestBase.get(`${PACKING_SLIP}/account-integrations`, config)
}

const SettingsService = {
  // addresses
  getAddress,
  saveAddress,
  editAddress,
  deleteAddress,

  // packages
  getPackages,
  getPackageDetail,
  createPackages,
  editPackage,
  deletePackage,

  // presets
  getShippingPresets,
  getDefaultPreset,
  createShippingPreset,
  updateShippingPreset,
  setDefaultPreset,
  applyShippingPreset,
  deleteShippingPreset,

  // store
  getConfig,
  frontToken,
  connections,
  connect,
  disconnect,
  removeStore,
  getShopifyIntegrateLink,
  getEbayConsentLink,
  webhookUrl,
  checkPromoEligibility,
  applyPromoCode,

  // profile
  accountEdit,
  changePassword,
  getCompany,
  changePlan,

  // general
  updateGeneral,
  getNotification,
  createNotification,
  updateNotification,

  // settings
  settings,
  updatePrint,
  updatePackingSlip,
  updateReport,
  updateManifest,

  // temu integration
  connectTemuStore,
  getFundingPreference,
  createFundingSetting,
  updateFundingSetting,

  // 2FA
  get2FASettings,
  update2FASettings,
  addPhoneNumber,
  removePhoneNumber,
  setDefaultPhoneNumber,

  getSettingInsurance,
  postSettingInsurance,
  putSettingInsurance,

  // carrier route
  getCarrierRoute,
  updateCarrierRoute,

  // account integrations
  getAccountIntegrations,
}

export default SettingsService
